import type { TradeItem } from "modules/tradeLists/model/tradeList";

/**
 * Quantity fields on a trade item arrive as strings from the API, and
 * `spokenForQuantity` is overlaid asynchronously by
 * `useBrowseItemAvailability` — so it can briefly exceed the listed quantity
 * while a page is mid-refresh. Every consumer needs the same clamping, hence
 * these three primitives rather than an ad-hoc `Number(...)` at each call site.
 */
type QuantityFields = {
  /** String on TradeItem (API shape), number on ProposalItem. */
  quantity: TradeItem["quantity"] | number;
  spokenForQuantity?: number;
};

/** Listed copies, floored at 1 — a listing always represents at least one card. */
export function totalQuantity(item: QuantityFields): number {
  return Math.max(1, Number(item.quantity) || 1);
}

/** Copies locked into an active proposal, clamped to what is actually listed. */
export function spokenForQuantity(item: QuantityFields): number {
  return Math.min(
    totalQuantity(item),
    Math.max(0, item.spokenForQuantity ?? 0)
  );
}

/**
 * Copies still free to put into a new proposal. Zero means every listed copy
 * is already spoken for, which is what disables selection and the single-card
 * proposal button on browse surfaces.
 *
 * `spokenForQuantity` is populated by useBrowseItemAvailability on TRADES-type
 * browse queries, and by the match endpoints themselves on matched-card
 * surfaces (the city matched view, the chat matched sidebar) — where it is
 * reported in the same unit as the row's tradeable `quantity`, not as a raw
 * count of the counterparty's reserved copies. It is absent only where no
 * endpoint supplies it, and consumers must fall back there.
 */
export function availableQuantity(item: QuantityFields): number {
  return totalQuantity(item) - spokenForQuantity(item);
}
